import React from 'react';
import { Box, Button, Chip, Typography } from '@mui/material';
import {
  PlayArrow as RunIcon,
  Close as CancelIcon,
} from '@mui/icons-material';
import { ApprovalDetails, NLTestPlan } from '../types';
import NLTestStepsViewer from './NLTestStepsViewer';

interface NLTestApprovalCardProps {
  approval: ApprovalDetails;
  nlPlan: NLTestPlan;
  onApprove: (approvalId: string) => void;
  onReject: (approvalId: string) => void;
}

const NLTestApprovalCard: React.FC<NLTestApprovalCardProps> = ({
  approval,
  nlPlan,
  onApprove,
  onReject,
}) => {
  const borderColor =
    approval.risk_level === 'high' || approval.risk_level === 'critical'
      ? 'error.main'
      : 'warning.main';
  const bgColor =
    approval.risk_level === 'high' || approval.risk_level === 'critical'
      ? 'error.light'
      : 'warning.light';

  return (
    <Box
      sx={{
        mt: 2,
        p: 2,
        border: '1px solid',
        borderColor,
        borderRadius: 2,
        bgcolor: bgColor,
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="subtitle2" fontWeight={700}>
          Review Test Steps
        </Typography>
        {approval.risk_level && (
          <Chip
            size="small"
            label={approval.risk_level.toUpperCase()}
            color={
              approval.risk_level === 'high' || approval.risk_level === 'critical'
                ? 'error'
                : 'warning'
            }
            variant="filled"
            sx={{ fontWeight: 600, fontSize: '0.7rem' }}
          />
        )}
        <Chip
          size="small"
          label={`${nlPlan.step_count || nlPlan.steps.length} steps`}
          variant="outlined"
          sx={{ fontSize: '0.7rem' }}
        />
      </Box>

      {/* Description */}
      <Typography variant="body2" sx={{ mb: 1.5 }}>
        {approval.reason || 'Review the generated test steps before execution.'}
      </Typography>

      {/* Base URL */}
      {nlPlan.base_url && (
        <Chip
          size="small"
          label={nlPlan.base_url}
          variant="outlined"
          sx={{ fontFamily: 'monospace', fontSize: '0.75rem', mb: 1.5 }}
        />
      )}

      {/* Steps viewer in compact mode */}
      <Box sx={{ bgcolor: 'background.paper', borderRadius: 1, p: 1, mb: 1.5 }}>
        <NLTestStepsViewer plan={nlPlan} compact />
      </Box>

      {/* Actions */}
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button
          size="small"
          variant="contained"
          color="success"
          startIcon={<RunIcon />}
          onClick={() => onApprove(approval.approval_id)}
          sx={{ fontWeight: 600 }}
        >
          Approve & Run
        </Button>
        <Button
          size="small"
          variant="outlined"
          color="error"
          startIcon={<CancelIcon />}
          onClick={() => onReject(approval.approval_id)}
        >
          Cancel
        </Button>
      </Box>
    </Box>
  );
};

export default NLTestApprovalCard;
